import * as typeform from '@typeform/embed-react';
import { AccountData } from '@types';
import { useMemo } from 'react';
import { mutate } from 'swr';
import { patch } from '@/utils/api';
import { alertErrorMessage } from '@/utils/alerts';
import { IUserContext, useUserContext } from '@/context/UserContext';
import { getUserFullName } from '@/components/UserList/utils';
import useAccountData from '@/hooks/useAccountData';

const { PopupButton } = typeform;

export interface TypeformActionButtonProps {
  clientData: AccountData;
  formId: string;
  endpoint: string;
  buttonText: string;
  buttonKey: string;
  errorMessage: string;
  extraHiddenProps?: Record<string, string>;
}

export function AccountDetailsTypeformActionButton({
  clientData,
  formId,
  endpoint,
  buttonText,
  buttonKey,
  errorMessage,
  extraHiddenProps = {},
}: TypeformActionButtonProps) {
  const { auth }: IUserContext = useUserContext();
  const { refreshAccountData } = useAccountData();

  const getHiddenProps = useMemo<Record<string, string>>((): Record<
    string,
    string
  > => {
    const userFullName = auth?.id ? getUserFullName(auth) : '';
    const companyName = (clientData.business_info.company_name as string) || '';

    return {
      company_name: companyName,
      user_name: userFullName,
      ...extraHiddenProps,
    };
  }, [clientData, auth, extraHiddenProps]);

  const onSubmit = async () => {
    try {
      const result = await patch<string>(endpoint, {
        id: clientData.id,
      });
      await refreshAccountData(clientData.id);
      const accountListAPIUrl = 'account/list';
      await mutate(accountListAPIUrl);
      return result;
    } catch (err) {
      alertErrorMessage(errorMessage);
      return '';
    }
  };

  return (
    <div className='my-3 flex items-center mb-4'>
      <PopupButton
        id={formId}
        hidden={getHiddenProps}
        transitiveSearchParams={['']}
        className='btn btn-accent btn-sm'
        key={buttonKey}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={onSubmit}
      >
        {buttonText}
      </PopupButton>
    </div>
  );
}
